import type { ReactNode } from 'react'
import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export type SettingsSectionVariant = 'default' | 'danger' | 'warning'

export type SettingsSectionProps = {
  title?: ReactNode
  description?: ReactNode
  /** Leading icon in the title row (16px). */
  icon?: ReactNode
  /** Outline badge next to the title (e.g. override count, Connected). */
  badge?: ReactNode
  /** Trailing header actions (ghost refresh, etc.). */
  actions?: ReactNode
  children?: ReactNode
  className?: string
  variant?: SettingsSectionVariant
  'data-testid'?: string
}

/**
 * Shared Settings content chrome.
 *
 * SoT: Basecamp Project folders / Listening
 * (`rounded-lg border border-border bg-muted/20 px-5 py-5`).
 * Gallery: /?test=maater-ui#settings-chrome
 * Bet: docs/product/bets/2026-08-18-settings-ui-chrome-pass.md
 */
export const SettingsSection = forwardRef<HTMLDivElement, SettingsSectionProps>(
  function SettingsSection(
    {
      title,
      description,
      icon,
      badge,
      actions,
      children,
      className,
      variant = 'default',
      'data-testid': testId,
    },
    ref,
  ) {
    const hasHeader = title != null || description != null || actions != null

    return (
      <div
        ref={ref}
        data-testid={testId}
        className={cn(
          'space-y-5 rounded-lg border px-5 py-5',
          variant === 'default' && 'border-border bg-muted/20',
          variant === 'danger' && 'border-destructive/30 bg-destructive/5',
          variant === 'warning' &&
            'border-amber-500/35 bg-amber-500/10 dark:border-amber-500/30 dark:bg-amber-500/10',
          className,
        )}
      >
        {hasHeader ? (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-2">
              {title != null ? (
                <div className="flex flex-wrap items-center gap-2 text-base font-semibold leading-snug">
                  {icon ? (
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">
                      {icon}
                    </span>
                  ) : null}
                  {title}
                  {badge}
                </div>
              ) : null}
              {description != null ? (
                <div className="text-sm leading-relaxed text-muted-foreground">{description}</div>
              ) : null}
            </div>
            {actions ? <div className="flex shrink-0 items-start gap-1 -mr-1">{actions}</div> : null}
          </div>
        ) : null}
        {children}
      </div>
    )
  },
)

SettingsSection.displayName = 'SettingsSection'

/**
 * Selected / on surface. Same primary wash as rail + ws-nav-item (~12%).
 * Never amber-950 (dark-only; muddy in light, invisible in dark).
 */
export const SETTINGS_SELECTED_SURFACE_CLASS =
  'border-primary/35 bg-primary/15'

/** Inner list / form row inside a SettingsSection (keys, emails, tokens). */
export function SettingsSectionRow({
  children,
  className,
  selected = false,
  'data-testid': testId,
}: {
  children: ReactNode
  className?: string
  /** On / enabled / current. Primary wash, not amber. */
  selected?: boolean
  'data-testid'?: string
}) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'rounded-md border px-3.5 py-3.5',
        selected
          ? SETTINGS_SELECTED_SURFACE_CLASS
          : 'border-border bg-background/60',
        className,
      )}
    >
      {children}
    </div>
  )
}
